import {useNuxtApp} from 'nuxt/app'
import {useUserStore} from '@/store/user'
import {useAuthStore} from '@/store/auth'
import type {User, UserManager} from 'oidc-client-ts'
import {useKeycloakService} from '~/composables/keycloak_service'
import {buildKeycloakUser, clearImpersonationToken} from './useKeycloakToken'

let keycloakRefreshPromise: Promise<string | null> | null = null

export function refreshKeycloakSession(user: User): Promise<string | null> {
  if (!keycloakRefreshPromise) {
    keycloakRefreshPromise = (async () => {
      const userStore = useUserStore()
      const authStore = useAuthStore()
      const userManager = useNuxtApp().$userManager

      let customerUuid = userStore.loadedCustomerUuid
      if (!customerUuid || customerUuid === 'null') {
        customerUuid = 'default'
      }

      const {refreshKeycloakToken} = useKeycloakService()
      const res = await refreshKeycloakToken(customerUuid, user.refresh_token!)
      if (res?.status === 200 && res.data?.accessToken) {
        const newAccessToken = res.data.accessToken
        const newUser = buildKeycloakUser(newAccessToken, res.data.idToken || user.id_token, res.data.refreshToken || user.refresh_token, user.state)
        await userManager.storeUser(newUser)
        authStore.setUserObject(newUser)
        // In an impersonation tab this reverts to the admin's own durable session once it expires.
        userStore.setAccessToken(newAccessToken)
        return newAccessToken
      }
      return null
    })().finally(() => { keycloakRefreshPromise = null })
  }
  return keycloakRefreshPromise
}

export function getKeycloakHomePath() {
  const app = useNuxtApp()
  const locale = app.$i18n.locale.value ?? 'en'
  const region = app.$globalization.getRegion(false) ?? 'eu'
  return app.$globalization.localePath('index', locale, region)
}

export function resolveRedirectPath(redirectPath?: string) {
  if (!redirectPath) {
    return '/eu/en'
  }

  const match = /customerUuid=([^&]+)/.exec(redirectPath)
  if (match?.[1]) {
    const userStore = useUserStore()
    userStore.customerUuidLink = match[1]
  }

  return redirectPath
}

export async function clearKeycloakState(userManager?: UserManager | null) {
  const authStore = useAuthStore()
  const userStore = useUserStore()

  authStore.setUserObject(null)
  userStore.accessToken = ''
  userStore.setLoggedInUsingKeycloakFlow(false)

  // Drop any per-tab impersonation session so a logout never leaves an impersonated token behind.
  clearImpersonationToken()

  if (userManager) {
    await userManager.removeUser()
  }

  // Remove the SSR "logged-in" marker cookie. Without this, a logout whose
  // SSO redirect is blocked (e.g. by CSP) leaves this cookie behind, and the
  // server keeps treating the user as logged in (see ssr-middleware.js).
  if (process.client) {
    useNuxtApp().$gtmNewSession?.()
    const {CookieStorage} = await import('cookie-storage')
    const cookieStorage = new CookieStorage({path: '/'})
    cookieStorage.removeItem('ENuxtKeycloakUser.sub')
  }
}
